import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, BookOpen, CalendarDays, Camera, Car, Clock, CreditCard,
  DollarSign, FileBadge, FileText, HelpCircle, Loader2, LogOut, Mail, Pencil, Phone,
  Settings, ShieldCheck, ShoppingBag, Star, Timer, Trash2, User,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChefHeader from '../shared/ui/ChefHeader';
import { EmptyState, ListRow, StatCard } from '../shared/ui';
import { listenToDriver, updateDriver, uploadUserPhoto } from '../shared/services/userService';
import { formatCurrency, formatDate, toDate } from '../shared/models';
import { maxDeliveryRadiusMiles } from '../components/work/geo';
import EditProfileSheet from '../components/profile/EditProfileSheet';
import VehicleSheet from '../components/profile/VehicleSheet';
import BankDetailsSheet from '../components/profile/BankDetailsSheet';
import SettingsSheet from '../components/profile/SettingsSheet';
import HelpSheet from '../components/profile/HelpSheet';
import LegalSheet from '../components/profile/LegalSheet';
import DeleteAccountSheet from '../components/profile/DeleteAccountSheet';
import DriverPreferencesSheet from '../components/profile/DriverPreferencesSheet';
import PermitSheet from '../components/profile/PermitSheet';
import DocumentSheet from '../components/profile/DocumentSheet';
import { requiredDriverDocs, docStatusLabel } from '../shared/documents';

const DOC_ICONS = { selfie: User, driversLicense: CreditCard, vehiclePhoto: Car, deliveryBag: ShoppingBag };

// Grouped white section — mirrors iOS List sections with header label.
function Section({ label, children }) {
  return (
    <div>
      <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100 overflow-hidden">
        {children}
      </div>
    </div>
  );
}

// iOS formatAverageDeliveryTime: seconds -> "Xh Ym" / "Ym" / "N/A".
function formatAvgDeliveryTime(v) {
  if (v == null || v === '') return 'N/A';
  if (typeof v === 'string') return v;
  const seconds = Number(v);
  if (!isFinite(seconds) || seconds <= 0) return 'N/A';
  const minutes = Math.round(seconds / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function ProfilePage() {
  const { user, userProfile, driver, driverId, signOut } = useAuth();

  // Local copy of the user profile so edits/photo uploads reflect instantly
  // (AuthContext fetches the profile once — mirrors iOS viewModel.profile).
  const [profile, setProfile] = useState(userProfile);
  useEffect(() => { setProfile(userProfile); }, [userProfile]);
  const mergeProfile = fields => setProfile(p => ({ ...(p || {}), ...fields }));

  // Live driver doc (stats, vehicle, online status) — mirrors iOS driverInfo.
  const [liveDriver, setLiveDriver] = useState(driver);
  useEffect(() => {
    if (!driverId) return undefined;
    return listenToDriver(driverId, d => setLiveDriver(d));
  }, [driverId]);
  const drv = liveDriver || driver;

  const [sheet, setSheet] = useState(null); // edit|vehicle|bank|settings|guide|support|terms|privacy|liability|permit
  const [activeDoc, setActiveDoc] = useState(null); // DRIVER_DOC_TYPES entry for DocumentSheet
  const [showPreferences, setShowPreferences] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const fileRef = useRef(null);

  const firstName = profile?.firstName || '';
  const lastName = profile?.lastName || '';
  const fullName =
    `${firstName} ${lastName}`.trim() ||
    profile?.name ||
    drv?.name ||
    `${drv?.raw?.firstName || ''} ${drv?.raw?.lastName || ''}`.trim() ||
    'Driver';
  const email = profile?.email || user?.email || drv?.email || '';
  const phone = profile?.phone || profile?.phoneNumber || drv?.phone || '';
  const photoURL = profile?.profileImageURL || profile?.photoURL || drv?.profileImageURL || '';
  const isOnline = !!drv?.isOnline;
  const hireDate = toDate(profile?.hireDate || profile?.createdAt || drv?.createdAt);

  // Performance stats from drivers/{uid} (live via listenToDriver).
  const totalDeliveries = drv?.totalDeliveries || 0;
  const rating = drv?.rating || 0;
  const rawOnTime = drv?.onTimeRate ?? 0;
  const onTimePct = Math.round(rawOnTime <= 1 ? rawOnTime * 100 : rawOnTime);
  const totalEarnings = drv?.totalEarnings ?? drv?.raw?.earnings ?? 0;
  const avgDeliveryTime = formatAvgDeliveryTime(drv?.averageDeliveryTime);

  async function onPhotoSelected(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user) return;
    setUploadingPhoto(true);
    setPhotoError('');
    try {
      const url = await uploadUserPhoto(user.uid, file);
      mergeProfile({ profileImageURL: url, photoURL: url });
      // Keep the drivers doc in sync (admin views read driver.profileImageURL).
      if (driverId) updateDriver(driverId, { profileImageURL: url }).catch(() => {});
    } catch (err) {
      console.error('photo upload', err);
      setPhotoError('Photo upload failed. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  }

  return (
    <div>
      <ChefHeader title="My Profile" />
      {!profile ? (
        <EmptyState
          icon={User}
          title="Not Signed In"
          subtitle="Please sign out and sign in again to access your profile."
        />
      ) : (
        <div className="px-4 py-4 space-y-5">
          {/* Profile photo & basic info */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingPhoto}
              className="relative w-20 h-20 shrink-0 rounded-full"
              aria-label="Change profile photo"
            >
              {photoURL ? (
                <img
                  src={photoURL}
                  alt={fullName}
                  className="w-20 h-20 rounded-full object-cover ring-2 ring-gray-200"
                />
              ) : (
                <span className="w-20 h-20 rounded-full bg-orange-50 ring-2 ring-gray-200 flex flex-col items-center justify-center gap-0.5">
                  <User size={26} className="text-[#FF6B35]" />
                  <span className="text-[9px] text-gray-400 font-medium">Tap to Upload</span>
                </span>
              )}
              {uploadingPhoto && (
                <span className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center">
                  <Loader2 size={22} className="animate-spin text-[#FF6B35]" />
                </span>
              )}
              <span className="absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full bg-[#FF6B35] text-white flex items-center justify-center ring-2 ring-white">
                <Camera size={13} />
              </span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPhotoSelected}
            />

            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 truncate">{fullName}</p>
              <p className="text-sm text-gray-500 truncate">{email}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${isOnline ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-300'}`} />
                  {isOnline ? 'Online' : 'Offline'}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-orange-50 text-[#FF6B35] text-[10px] font-bold uppercase tracking-wide">
                  Driver
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setSheet('edit')}
              className="w-9 h-9 rounded-full bg-orange-50 text-[#FF6B35] flex items-center justify-center shrink-0 active:bg-orange-100"
              aria-label="Edit profile"
            >
              <Pencil size={17} />
            </button>
          </div>
          {photoError && <p className="text-sm text-red-500 -mt-3 px-1">{photoError}</p>}

          {/* Performance stats (live from drivers/{uid}) */}
          <div>
            <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Performance
            </p>
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Total Deliveries" value={totalDeliveries} icon={Car} />
              <StatCard label="Avg Rating" value={rating ? rating.toFixed(1) : '0.0'} icon={Star} />
              <StatCard label="On-Time Rate" value={`${onTimePct}%`} icon={Clock} />
              <StatCard label="Total Earnings" value={formatCurrency(totalEarnings)} icon={DollarSign} color="text-green-600" />
              <StatCard label="Avg Delivery Time" value={avgDeliveryTime} icon={Timer} />
            </div>
          </div>

          {/* Contact Information */}
          <Section label="Contact Information">
            <ListRow icon={User} label="Name" value={fullName} onClick={() => setSheet('edit')} />
            <ListRow icon={Mail} label="Email" value={email || '—'} onClick={() => setSheet('edit')} />
            <ListRow icon={Phone} label="Phone" value={phone || 'Add phone'} onClick={() => setSheet('edit')} />
            {hireDate && (
              <ListRow icon={CalendarDays} label="Joined" value={formatDate(hireDate)} chevron={false} />
            )}
          </Section>

          {/* Delivery Information */}
          <Section label="Delivery">
            <ListRow
              icon={Car}
              label="Delivery Type"
              value={
                drv?.vehicleType
                  ? `${drv.vehicleType}${drv.vehicleNumber ? ` - ${drv.vehicleNumber}` : ''}`
                  : 'Add delivery type'
              }
              onClick={() => setSheet('vehicle')}
            />
            {maxDeliveryRadiusMiles(drv?.vehicleType) != null ? (
              <ListRow
                icon={Timer}
                label="Delivery Range"
                value={`${maxDeliveryRadiusMiles(drv.vehicleType)} mi`}
                chevron={false}
              />
            ) : (
              <ListRow
                icon={CreditCard}
                label="License Number"
                value={drv?.licenseNumber || 'Add license'}
                onClick={() => setSheet('vehicle')}
              />
            )}
          </Section>

          {/* Payout */}
          <Section label="Payout">
            <ListRow
              icon={CreditCard}
              label="Payout Details"
              value={
                drv?.bankName && drv?.bankAccountNumber
                  ? `${drv.bankName} ··${String(drv.bankAccountNumber).slice(-4)}`
                  : 'Add bank details'
              }
              onClick={() => setSheet('bank')}
            />
          </Section>

          {/* Documents — verification docs mirror iOS DriverDocumentsView:
              selfie + delivery bag for everyone, license + vehicle photo
              for motorized types and bicycles. */}
          <Section label="Documents">
            {requiredDriverDocs(drv?.vehicleType).map(doc => (
              <ListRow
                key={doc.key}
                icon={DOC_ICONS[doc.key] || Camera}
                label={doc.title}
                value={docStatusLabel(drv, doc)}
                onClick={() => setActiveDoc(doc)}
              />
            ))}
            <ListRow
              icon={FileBadge}
              label="Food Handler's Permit"
              value={
                drv?.foodHandlerPermitStatus === 'approved' ? 'Approved'
                : drv?.foodHandlerPermitURL ? 'Pending Review'
                : 'Required'
              }
              onClick={() => setSheet('permit')}
            />
          </Section>

          {/* App Settings */}
          <Section label="App Settings">
            <ListRow
              icon={Settings}
              label="Preferences & Settings"
              value={profile?.preferredNavigationApp || ''}
              onClick={() => setSheet('settings')}
            />
            <ListRow
              icon={Settings}
              label="Driver Preferences"
              onClick={() => setShowPreferences(true)}
            />
          </Section>

          {/* Help & Information */}
          <Section label="Help & Information">
            <ListRow icon={BookOpen} label="User Guide & FAQ" onClick={() => setSheet('guide')} />
            <ListRow icon={HelpCircle} label="Help & Support" onClick={() => setSheet('support')} />
          </Section>

          {/* Legal */}
          <Section label="Legal">
            <ListRow icon={FileText} label="Terms of Service" onClick={() => setSheet('terms')} />
            <ListRow icon={ShieldCheck} label="Privacy Policy" onClick={() => setSheet('privacy')} />
            <ListRow icon={AlertTriangle} label="Liability Waiver" onClick={() => setSheet('liability')} />
          </Section>

          {/* Account Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <ListRow icon={LogOut} label="Sign Out" destructive chevron={false} onClick={() => signOut()} />
          </div>

          <p className="text-center text-[11px] text-gray-400 pb-2">
            CheF SKN Driver · v1.0.0 · Operated by Browne Ink · Support +1 (869) 660-1052
          </p>
        </div>
      )}

      <EditProfileSheet
        open={sheet === 'edit'}
        onClose={() => setSheet(null)}
        uid={user?.uid}
        profile={profile}
        onSaved={mergeProfile}
      />
      <VehicleSheet
        open={sheet === 'vehicle'}
        onClose={() => setSheet(null)}
        driverId={driverId}
        driver={drv}
      />
      <BankDetailsSheet
        open={sheet === 'bank'}
        onClose={() => setSheet(null)}
        driverId={driverId}
        driver={drv}
      />
      <SettingsSheet
        open={sheet === 'settings'}
        onClose={() => setSheet(null)}
        uid={user?.uid}
        profile={profile}
        onSaved={mergeProfile}
      />
      <HelpSheet open={sheet === 'guide'} onClose={() => setSheet(null)} mode="guide" />
      <HelpSheet open={sheet === 'support'} onClose={() => setSheet(null)} mode="support" />
      <LegalSheet open={sheet === 'terms'} onClose={() => setSheet(null)} doc="terms" />
      <LegalSheet open={sheet === 'privacy'} onClose={() => setSheet(null)} doc="privacy" />
      <LegalSheet open={sheet === 'liability'} onClose={() => setSheet(null)} doc="liability" />
      <DriverPreferencesSheet
        open={showPreferences}
        onClose={() => setShowPreferences(false)}
        driver={drv}
      />
      <PermitSheet
        open={sheet === 'permit'}
        onClose={() => setSheet(null)}
        driverId={driverId}
        driver={drv}
      />
      <DocumentSheet
        open={!!activeDoc}
        onClose={() => setActiveDoc(null)}
        driverId={driverId}
        driver={drv}
        doc={activeDoc}
      />
    </div>
  );
}
